<template>
  <div>
    <!-- Fixed 模式：固定显示 -->
    <div
      v-if="mode === 'fixed'"
      :id="containerId"
      class="verify-container"
      :class="{ 'verify-wrapper': true, mobile: ismobileModel }"
    >
      <verify-slide
        ref="verifySlide"
        :captchaType="captchaType"
        type="2"
        mode="fixed"
        :imgSize="imgSize"
        :barSize="barSize"
        :blockSize="blockSize"
        :vSpace="vSpace"
        :explain="explain"
        @ready="handleReady"
        @success="handleSuccess"
        @error="handleError"
      />
    </div>

    <!-- Pop 模式：弹窗显示 -->
    <el-dialog
      v-if="mode === 'pop'"
      v-model="dialogVisible"
      :title="'验证码'"
      :width="dialogWidth"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      class="verify-dialog"
    >
      <verify-slide
        ref="verifySlide"
        :captchaType="captchaType"
        type="2"
        mode="pop"
        :imgSize="imgSize"
        :barSize="barSize"
        :blockSize="blockSize"
        :vSpace="vSpace"
        :explain="explain"
        @ready="handleReady"
        @success="handleSuccess"
        @error="handleError"
      />
      <template #footer>
        <el-button @click="closeDialog">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import VerifySlide from "./Verify/VerifySlide.vue";
import { isMobile } from "@components/verifition/utils/util";

export default {
  name: "Verify",
  components: {
    VerifySlide,
  },
  props: {
    // 容器 ID
    containerId: {
      type: String,
      default: "verify-container",
    },
    // 展示模式
    mode: {
      type: String,
      default: "fixed", // 'fixed' 或 'pop'
      validator: (value) => ["fixed", "pop"].includes(value),
    },
    // 验证码类型
    captchaType: {
      type: String,
      default: "blockPuzzle", // 'blockPuzzle' 或 'clickWord'
    },
    // 图片大小
    imgSize: {
      type: Object,
      default: () => ({ width: "310px", height: "155px" }),
    },
    // 滑块大小
    barSize: {
      type: Object,
      default: () => ({ width: "310px", height: "40px" }),
    },
    // 拼图块大小
    blockSize: {
      type: Object,
      default: () => ({ width: "50px", height: "50px" }),
    },
    // 图片和滑块之间的间距
    vSpace: {
      type: Number,
      default: 5,
    },
    // 提示文字
    explain: {
      type: String,
      default: "向右滑动完成验证",
    },
  },
  emits: ["ready", "success", "error"],
  data() {
    return {
      dialogVisible: false,
      // 回调函数
      beforeCheck: null,
      readyCallback: null,
      successCallback: null,
      errorCallback: null,
    };
  },
  computed: {
    dialogWidth() {
      const width = parseInt(this.imgSize.width) || 310;
      return Math.max(400, width + 100) + "px";
    },
    ismobileModel() {
      return isMobile();
    },
  },
  mounted() {
    // Pop 模式：如果指定了 popContainerId，则自动绑定点击事件
    if (this.mode === "pop" && this.popContainerId) {
      this.bindPopClick();
    }
  },
  beforeUnmount() {
    // 清理事件监听
    if (this.popClickHandler) {
      const element = document.getElementById(this.popContainerId);
      if (element) {
        element.removeEventListener("click", this.popClickHandler);
      }
    }
  },
  methods: {
    // 初始化配置（类似 jQuery 的配置方式）
    init(options = {}) {
      // 设置配置选项
      if (options.beforeCheck && typeof options.beforeCheck === "function") {
        this.beforeCheck = options.beforeCheck;
      }
      if (options.ready && typeof options.ready === "function") {
        this.readyCallback = options.ready;
      }
      if (options.success && typeof options.success === "function") {
        this.successCallback = options.success;
      }
      if (options.error && typeof options.error === "function") {
        this.errorCallback = options.error;
      }
    },
    // Pop 模式：绑定点击事件
    bindPopClick() {
      this.$nextTick(() => {
        const element = document.getElementById(this.popContainerId);
        if (element) {
          this.popClickHandler = () => {
            // 执行 beforeCheck 回调
            if (this.beforeCheck) {
              const flag = this.beforeCheck();
              if (!flag) {
                return;
              }
            }
            this.show();
          };
          element.addEventListener("click", this.popClickHandler);
        }
      });
    },
    show() {
      // Pop 模式：显示弹窗
      if (this.mode === "pop") {
        this.dialogVisible = true;
      }
    },
    closeDialog() {
      this.dialogVisible = false;
      if (this.$refs.verifySlide) {
        this.$refs.verifySlide.refresh();
      }
    },
    handleReady() {
      // 触发 ready 回调
      if (this.readyCallback) {
        this.readyCallback();
      }
      this.$emit("ready");
    },
    handleSuccess(params) {
      // 触发成功回调
      if (this.successCallback) {
        this.successCallback(params);
      }
      this.$emit("success", params);

      // Pop 模式：验证成功后自动关闭弹窗
      if (this.mode === "pop") {
        setTimeout(() => {
          this.dialogVisible = false;
        }, 1000);
      }
    },
    handleError() {
      // 触发失败回调
      if (this.errorCallback) {
        this.errorCallback();
      }
      this.$emit("error");
    },
    refresh() {
      // 刷新验证码
      if (this.$refs.verifySlide) {
        this.$refs.verifySlide.refresh();
      }
    },
  },
};
</script>

<style scoped>
.verify-container {
  width: 100%;
}
@media (max-width: 768px) {
  .verify-wrapper {
    padding: 0 10px;
  }

  .verify-dialog :deep(.el-dialog) {
    width: 90% !important;
    margin: 20px auto !important;
  }
}

/* 移动端特殊样式 */
.verify-wrapper.mobile {
  --mobile-img-height: 200px;
  --mobile-bar-height: 60px;
}
.verify-dialog :deep(.el-dialog__body) {
  padding: 20px;
}
</style>
